import { formatCurrency } from './stampDutyCalculator';

export interface UteTruckLoanInputs {
  vehiclePrice: number;          // Purchase price of the ute/truck
  downPayment: number;           // Initial deposit
  tradeInValue: number;          // Value of trade-in vehicle
  accessories: number;           // Cost of accessories (bull bar, canopy, etc.)
  utilizationCategory: string;   // 'private', 'business', 'primary-producer'
  loanTerm: number;              // Loan term in months
  interestRate: number;          // Annual interest rate (%)
  balloonPayment: number;        // Optional balloon payment at end
  applicationFee: number;        // Loan application fee
  annualFee: number;             // Annual loan maintenance fee
  isCommercial: boolean;         // Whether it's registered as a commercial vehicle
  isNewVehicle: boolean;         // Whether the ute/truck is new or used
}

export interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  fees: number;
  taxDeduction?: number;
}

export interface UteTruckLoanResult {
  monthlyPayment: number;
  totalInterest: number;
  totalFees: number;
  totalCost: number;
  loanAmount: number;
  amortizationSchedule: AmortizationRow[];
  comparisonRate: number;
  totalAccessoryCost: number;
  estimatedRunningCost: RunningCostEstimate;
  taxBenefits?: TaxBenefitEstimate;
}

export interface RunningCostEstimate {
  fuelCostPer1000km: number;
  annualRegistration: number;
  annualInsurance: number;
  annualMaintenance: number;
  annualTires: number;
  totalAnnualCost: number;
}

export interface TaxBenefitEstimate {
  depreciationYearOne: number;
  interestDeduction: number;
  gstCredit?: number;
  instantAssetWriteOff: boolean;
  totalFirstYearBenefit: number;
  effectiveInterestRate: number;
}

// Base running costs estimates by vehicle type
export const RUNNING_COST_ESTIMATES = {
  smallUte: {
    fuelConsumption: 8.5,        // L/100km
    registration: 600,           // Annual registration cost
    insurance: 1200,             // Annual insurance cost
    maintenance: 800,            // Annual maintenance cost
    tires: 800                   // Annual tire cost (average)
  },
  mediumUte: {
    fuelConsumption: 10.5,
    registration: 750,
    insurance: 1500,
    maintenance: 1000,
    tires: 1000
  },
  largeUte: {
    fuelConsumption: 12.5,
    registration: 900,
    insurance: 1800,
    maintenance: 1200,
    tires: 1200
  },
  lightTruck: {
    fuelConsumption: 14,
    registration: 1200,
    insurance: 2200,
    maintenance: 1800,
    tires: 1500
  },
  mediumTruck: {
    fuelConsumption: 18,
    registration: 1800,
    insurance: 2800,
    maintenance: 2500,
    tires: 2000
  },
  heavyTruck: {
    fuelConsumption: 25,
    registration: 3000,
    insurance: 3500,
    maintenance: 4000,
    tires: 3500
  }
};

// Cost modifier based on vehicle age
export const AGE_MODIFIERS = {
  new: 1.0,                     // New vehicle costs (baseline)
  recent: 1.2,                  // 1-3 years old (higher maintenance)
  older: 1.5                    // 4+ years old (higher maintenance)
};

// Tax rates by utilization category
export const TAX_RATES = {
  'private': 0,                 // No tax deductions for private use
  'business': 0.25,             // Assuming 25% business use
  'business-full': 0.90,        // Assuming 90% business use
  'primary-producer': 1.0       // Primary producers get 100% deduction
};

/**
 * Calculate ute/truck loan details based on inputs
 */
export function calculateUteTruckLoan(
  inputs: UteTruckLoanInputs, 
  vehicleType: keyof typeof RUNNING_COST_ESTIMATES = 'mediumUte',
  fuelPrice: number = 1.9,      // Average fuel price per liter
  averageKmPerYear: number = 20000  // Estimated distance per year
): UteTruckLoanResult {
  // Calculate total loan amount
  const totalAccessories = inputs.accessories;
  const loanAmount = inputs.vehiclePrice + totalAccessories - inputs.downPayment - inputs.tradeInValue + inputs.applicationFee;
  
  // Calculate monthly interest rate
  const monthlyRate = inputs.interestRate / 100 / 12;
  
  // Calculate monthly payment (accounting for balloon payment)
  let monthlyPayment = 0;
  if (monthlyRate === 0) {
    // Edge case: 0% interest
    monthlyPayment = (loanAmount - inputs.balloonPayment) / inputs.loanTerm;
  } else {
    // Standard formula with balloon payment
    monthlyPayment = (loanAmount - (inputs.balloonPayment / Math.pow(1 + monthlyRate, inputs.loanTerm))) 
                    * (monthlyRate * Math.pow(1 + monthlyRate, inputs.loanTerm)) 
                    / (Math.pow(1 + monthlyRate, inputs.loanTerm) - 1);
  }
  
  // Calculate tax benefits if applicable
  const taxBenefits = calculateTaxBenefits(
    inputs.utilizationCategory,
    loanAmount,
    inputs.interestRate,
    inputs.loanTerm,
    inputs.isNewVehicle,
    inputs.isCommercial
  );
  
  // Generate amortization schedule with tax deductions
  const amortizationSchedule = generateAmortizationSchedule(
    loanAmount,
    monthlyRate,
    inputs.loanTerm,
    monthlyPayment,
    inputs.balloonPayment,
    inputs.annualFee,
    inputs.utilizationCategory !== 'private' ? taxBenefits : undefined
  );
  
  // Calculate total interest and fees
  const totalInterest = amortizationSchedule.reduce((sum, row) => sum + row.interest, 0);
  const totalFees = inputs.applicationFee + (Math.ceil(inputs.loanTerm / 12) * inputs.annualFee);
  
  // Calculate comparison rate
  const comparisonRate = calculateComparisonRate(
    loanAmount,
    inputs.interestRate,
    inputs.loanTerm,
    inputs.applicationFee,
    inputs.annualFee
  );
  
  // Calculate running cost estimates
  const runningCostEstimate = calculateRunningCosts(
    vehicleType,
    inputs.isNewVehicle ? 'new' : 'recent',
    fuelPrice,
    averageKmPerYear,
    inputs.isCommercial
  );
  
  return {
    monthlyPayment,
    totalInterest,
    totalFees,
    totalCost: loanAmount + totalInterest + inputs.annualFee * Math.ceil(inputs.loanTerm / 12),
    loanAmount,
    amortizationSchedule,
    comparisonRate,
    totalAccessoryCost: totalAccessories,
    estimatedRunningCost: runningCostEstimate,
    taxBenefits: inputs.utilizationCategory !== 'private' ? taxBenefits : undefined
  };
}

/**
 * Generate amortization schedule for the loan
 */
function generateAmortizationSchedule(
  principal: number,
  monthlyRate: number,
  totalPeriods: number,
  monthlyPayment: number,
  balloonPayment: number,
  annualFee: number,
  taxBenefits?: TaxBenefitEstimate
): AmortizationRow[] {
  const schedule: AmortizationRow[] = [];
  let balance = principal;
  
  // Calculate monthly tax benefit if applicable
  const businessDeductionRate = taxBenefits ? 0.30 : 0; // Assuming 30% business tax rate
  
  for (let month = 1; month <= totalPeriods; month++) {
    // Calculate interest for this period
    const interestPayment = balance * monthlyRate;
    
    // Check if this is the last payment
    const isLastPayment = month === totalPeriods;
    const actualPayment = isLastPayment ? monthlyPayment + balloonPayment : monthlyPayment;
    
    // Calculate principal payment
    let principalPayment = monthlyPayment - interestPayment;
    
    // Adjust for last payment with balloon
    if (isLastPayment) {
      principalPayment = balance - balloonPayment;
    }
    
    // Calculate fees for this period
    const fees = month % 12 === 0 ? annualFee : 0;
    
    // Calculate tax deduction if applicable
    const taxDeduction = taxBenefits ? 
      (interestPayment * businessDeductionRate) + 
      (month <= 12 ? taxBenefits.depreciationYearOne / 12 : 0) : 
      undefined;
    
    // Update remaining balance
    balance = balance - principalPayment;
    
    // For the last payment, ensure balance is zeroed out with balloon
    if (isLastPayment) {
      balance = 0;
    }
    
    // Add row to schedule
    schedule.push({
      month,
      payment: actualPayment,
      principal: principalPayment,
      interest: interestPayment,
      balance,
      fees,
      taxDeduction
    });
  }
  
  return schedule;
}

/**
 * Calculate tax benefits for business vehicle use
 */
function calculateTaxBenefits(
  utilizationCategory: string,
  loanAmount: number,
  interestRate: number,
  loanTerm: number,
  isNewVehicle: boolean,
  isCommercial: boolean
): TaxBenefitEstimate {
  const taxRate = utilizationCategory === 'primary-producer' ? 0.30 : 0.25; // Simplified tax rates
  const businessUsePercentage = TAX_RATES[utilizationCategory as keyof typeof TAX_RATES] || 0;
  
  // Check for instant asset write-off eligibility
  const instantAssetWriteOff = isEligibleForInstantAssetWriteOff(
    loanAmount,
    utilizationCategory === 'primary-producer',
    isCommercial
  );
  
  // Calculate first year depreciation
  const depreciationYearOne = instantAssetWriteOff ?
    loanAmount * businessUsePercentage * taxRate :
    (loanAmount * 0.15 * businessUsePercentage * taxRate); // Simplified 15% depreciation in first year
  
  // Calculate interest deduction for first year
  const annualInterestRate = interestRate / 100;
  const totalInterestFirstYear = loanAmount * annualInterestRate; // Simplified
  const interestDeduction = totalInterestFirstYear * businessUsePercentage * taxRate;
  
  // GST credit (if applicable and registered for GST)
  const gstCredit = isCommercial && businessUsePercentage > 0 ?
    (loanAmount / 11) * businessUsePercentage : // GST is 1/11th of price in Australia
    undefined;
  
  // Total first year benefit
  const totalFirstYearBenefit = depreciationYearOne + interestDeduction + (gstCredit || 0);
  
  // Calculate effective interest rate
  const effectiveInterestRate = annualInterestRate - (annualInterestRate * businessUsePercentage * taxRate);
  
  return {
    depreciationYearOne,
    interestDeduction,
    gstCredit,
    instantAssetWriteOff,
    totalFirstYearBenefit,
    effectiveInterestRate: effectiveInterestRate * 100 // Convert back to percentage
  };
}

/**
 * Calculate running costs for utes and trucks
 */
function calculateRunningCosts(
  vehicleType: keyof typeof RUNNING_COST_ESTIMATES,
  ageCategory: keyof typeof AGE_MODIFIERS,
  fuelPrice: number,
  annualKm: number,
  isCommercial: boolean
): RunningCostEstimate {
  const baseEstimates = RUNNING_COST_ESTIMATES[vehicleType];
  const ageModifier = AGE_MODIFIERS[ageCategory] || 1.0;
  
  // Commercial vehicles have higher registration but may have lower insurance
  const registrationMultiplier = isCommercial ? 1.2 : 1.0;
  const insuranceMultiplier = isCommercial ? 0.9 : 1.0; // Commercial insurance can be cheaper with fleet policies
  
  // Calculate fuel cost per 1000km
  const fuelCostPer1000km = (baseEstimates.fuelConsumption / 100) * 1000 * fuelPrice;
  
  // Apply age modifier to maintenance and tire costs
  const maintenance = baseEstimates.maintenance * ageModifier;
  const tires = baseEstimates.tires * ageModifier;
  
  // Calculate total annual cost
  const totalAnnualCost = 
    (baseEstimates.registration * registrationMultiplier) + 
    (baseEstimates.insurance * insuranceMultiplier) +
    maintenance +
    tires +
    (fuelCostPer1000km * (annualKm / 1000));
  
  return {
    fuelCostPer1000km,
    annualRegistration: baseEstimates.registration * registrationMultiplier,
    annualInsurance: baseEstimates.insurance * insuranceMultiplier,
    annualMaintenance: maintenance,
    annualTires: tires,
    totalAnnualCost
  };
}

/**
 * Check eligibility for instant asset write-off
 */
function isEligibleForInstantAssetWriteOff(
  assetValue: number,
  isPrimaryProducer: boolean,
  isCommercial: boolean
): boolean {
  // Current threshold as of 2023 (check current ATO guidelines for updates)
  const threshold = isPrimaryProducer ? 160000 : 20000;
  
  // Must be for business use and under threshold
  return isCommercial && assetValue <= threshold;
}

/**
 * Calculate the comparison rate for the loan
 */
export function calculateComparisonRate(
  loanAmount: number,
  interestRate: number,
  loanTerm: number,
  applicationFee: number,
  annualFee: number
): number {
  // Simplified calculation for illustrative purposes
  const totalFees = applicationFee + (annualFee * Math.ceil(loanTerm / 12));
  const feeImpact = (totalFees / loanAmount) * 100 * (12 / loanTerm);
  
  return interestRate + feeImpact;
}

/**
 * Determine vehicle size category based on price
 */
export function getVehicleSizeByPrice(price: number, isTruck: boolean): keyof typeof RUNNING_COST_ESTIMATES {
  if (isTruck) {
    if (price < 50000) return 'lightTruck';
    if (price < 100000) return 'mediumTruck';
    return 'heavyTruck';
  } else {
    if (price < 35000) return 'smallUte';
    if (price < 60000) return 'mediumUte';
    return 'largeUte';
  }
} 